import { ClientOnly, createFileRoute } from "@tanstack/react-router";
import { useCallback, useState } from "react";

import { Button } from "@/web/components/primitives/button";
import {
  BUSINESS_CATEGORIES_AND_TYPES,
  getTagMetadata,
} from "@/web/lib/schemaUtils/businessCategories";
import { cn } from "@/web/lib/styling";
import { MarketingNavbar } from "@/web/routes/-marketing/MarketingNavbar";
import {
  BrowseFiltersBar,
  type BrowseViewMode,
} from "@/web/routes/browse/-components/BrowseFiltersBar";
import { BrowseMap } from "@/web/routes/browse/-components/BrowseMap";
import { BrowseStageTabs } from "@/web/routes/browse/-components/BrowseStageTabs";
import { CampaignResultsContainer } from "@/web/routes/browse/-components/CampaignResultsContainer";
import { FilterChip } from "@/web/routes/browse/-components/FilterChip";
import type { BrowseSearch, BrowseSort, DefaultStage } from "@/web/routes/browse/-components/types";
import {
  useCampaignBrowser,
  useCampaignBrowserSearch,
} from "@/web/routes/browse/-components/useBrowse";

const VALID_STAGES = new Set<DefaultStage>(["nomination", "roadshow", "offering"]);
const VALID_SORTS = new Set<BrowseSort>(["recent", "alphabetical", "alphabetical-desc"]);

function validateBrowseSearch(raw: Record<string, unknown>): BrowseSearch {
  return {
    fullText: typeof raw.fullText === "string" ? raw.fullText || undefined : undefined,
    stages: Array.isArray(raw.stages)
      ? raw.stages.filter(
          (s): s is DefaultStage => typeof s === "string" && VALID_STAGES.has(s as DefaultStage),
        )
      : undefined,
    categories: Array.isArray(raw.categories)
      ? raw.categories.filter((c): c is string => typeof c === "string")
      : undefined,
    tags: Array.isArray(raw.tags)
      ? raw.tags.filter((t): t is string => typeof t === "string")
      : undefined,
    sort:
      typeof raw.sort === "string" && VALID_SORTS.has(raw.sort as BrowseSort)
        ? (raw.sort as BrowseSort)
        : undefined,
  };
}

export const Route = createFileRoute("/browse/")({
  component: RouteComponent,
  validateSearch: validateBrowseSearch,
});

function RouteComponent() {
  const campaignBrowserRet = useCampaignBrowser();
  const { isDirty, clearSearch, search, removeTag, removeCategory, clearFullText, patchFullText } =
    useCampaignBrowserSearch();

  const [showSearch, setShowSearch] = useState(false);
  const [viewMode, setViewMode] = useState<BrowseViewMode>("grid");

  const handleSearchClick = useCallback(() => {
    setShowSearch(true);
  }, []);

  const searchExpanded = showSearch || Boolean(search.fullText);

  return (
    <div className="min-h-screen bg-background">
      <ClientOnly>
        <MarketingNavbar solid position="sticky" />
      </ClientOnly>

      <div className="px-4 py-6 md:px-8">
        <div className="flex min-h-25 flex-col justify-center gap-4">
          <BrowseStageTabs className="-mx-4 px-4 md:mx-0 md:px-0" />

          <BrowseFiltersBar
            className="-mx-4 px-4 md:mx-0 md:px-0"
            onSearchClick={handleSearchClick}
            searchExpanded={searchExpanded}
            searchText={search.fullText}
            onClearSearch={clearFullText}
            onSearchChange={patchFullText}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
          />

          <div className="no-scrollbar flex items-center gap-2 overflow-x-scroll">
            {search.tags?.map((t) => (
              <FilterChip
                key={t}
                label={getTagMetadata(t).friendlyName}
                onRemove={() => removeTag(t)}
              />
            ))}
            {search.categories?.map((c) => {
              const categoryDef =
                BUSINESS_CATEGORIES_AND_TYPES[c as keyof typeof BUSINESS_CATEGORIES_AND_TYPES];
              if (!categoryDef) return null;
              const Icon = categoryDef.icon;
              return (
                <FilterChip
                  key={c}
                  label={categoryDef.friendlyName}
                  icon={<Icon className="size-3.5" />}
                  onRemove={() => removeCategory(c)}
                />
              );
            })}
            <Button
              variant="ghost"
              onClick={clearSearch}
              className={cn(
                "font-withus-sans h-9 rounded-none border-gray-300 px-3 text-sm font-medium text-gray-400 [--button-border-width:1px] hover:border-gray-400 hover:text-gray-500",
                !isDirty && "hidden",
              )}
            >
              Clear filters
            </Button>
          </div>
        </div>

        <div className="py-4">
          {viewMode === "map" ? (
            <BrowseMap results={campaignBrowserRet.results} />
          ) : (
            <CampaignResultsContainer useCampaignBrowserRet={campaignBrowserRet} />
          )}
        </div>
      </div>
    </div>
  );
}
